import { useMemo } from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import type { TermsRow } from '@/services/api/terms';
import { uniqueSorted } from '@/utils/array';
import { BackgroundLoadingIndicator } from './background-loading-indicator';
import { TERMS_COLUMNS } from './terms-columns';
import { TermsFilterBar, type TermsFilterState } from './terms-filter-bar';
import { TermsSearchControls, type TermsSearchValue } from './terms-search-controls';

// Legacy items-per-page
const TERMS_PAGE_SIZE = 100;

interface TermsTabPanelProps {
  /** Full loaded dataset (unfiltered) — drives the background-loading caption */
  rows: TermsRow[];
  /** Rows after client-side column filters — what the table renders */
  filteredRows: TermsRow[];
  filters: TermsFilterState;
  onFiltersChange: (filters: TermsFilterState) => void;
  /** Blocking first-page load (overlay drops after the first page with data) */
  isLoading: boolean;
  isBackgroundLoading: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  search: TermsSearchValue;
  onSearch: (value: TermsSearchValue) => void;
  isSearching: boolean;
  hasVersionSearch: boolean;
  idPrefix: string;
}

export const TermsTabPanel = ({
  rows,
  filteredRows,
  filters,
  onFiltersChange,
  isLoading,
  isBackgroundLoading,
  errorMessage,
  onRetry,
  search,
  onSearch,
  isSearching,
  hasVersionSearch,
  idPrefix,
}: TermsTabPanelProps) => {
  // multi-choice filter options are derived from the loaded data
  const versionOptions = useMemo(() => uniqueSorted(rows.map((row) => row.version)), [rows]);

  return (
    <div className="flex flex-col gap-3">
      <TermsSearchControls active={search} hasVersion={hasVersionSearch} idPrefix={idPrefix} isSearching={isSearching} onSearch={onSearch} />
      <TermsFilterBar filters={filters} onChange={onFiltersChange} versionOptions={versionOptions} />
      {errorMessage ? <QueryErrorBanner message={errorMessage} onRetry={onRetry} title="Failed to load terms" /> : null}
      {/* Shown only while background loading is active AND at least one row is displayed */}
      {isBackgroundLoading && rows.length > 0 ? <BackgroundLoadingIndicator loadedCount={rows.length} /> : null}
      <DataTable columns={TERMS_COLUMNS} data={filteredRows} emptyMessage="No terms records found" isLoading={isLoading} pageSize={TERMS_PAGE_SIZE} />
    </div>
  );
};
TermsTabPanel.displayName = 'TermsTabPanel';
